import { Controller, useFormContext } from 'react-hook-form';
import { Button, Checkbox } from '@sravni/react-design-system';
import React, { useCallback, useMemo } from 'react';
import {
    amountTransform,
    Col,
    PhoneInput,
    phoneTransform,
    Row,
    Rows,
    TextInput,
} from '../../../../../../../landing.common';
import { FORM_FIELDS } from '../../../../../../../../constants';
import { RegionField } from '../../../../../../../form.common/fields';
import { GosuslugiAuth } from '../../../../../../../gosuslugi';
import { FullNameSuggest } from '../../../../../../../form/ui/suggest/full_name';
import { useIsPage } from '../../../../../../../../store/selectors/selection';
import { useLoggedIn } from '../../../../../../../../store/reducers/user';

export const StepFields = () => {
    const { isMainCreditCardPage } = useIsPage();
    const isLoggedIn = useLoggedIn();
    const showGosuslugiAuth = isMainCreditCardPage && !isLoggedIn;
    const form = useFormContext();
    const {
        control,
        formState: { errors },
        setValue,
        trigger,
        watch,
        getValues,
    } = form;

    const [patronymic] = watch([FORM_FIELDS.MIDDLE_NAME]);

    const onSelectFullName: React.ComponentProps<typeof FullNameSuggest>['onSelect'] = useCallback(
        async (option) => {
            setValue(FORM_FIELDS.FIRST_NAME, option.firstName || '');
            setValue(FORM_FIELDS.LAST_NAME, option.lastName || '');
            setValue(FORM_FIELDS.MIDDLE_NAME, option.patronymic || null);
            setValue(FORM_FIELDS.GENDER, option.gender || '');
            await trigger([FORM_FIELDS.LAST_NAME, FORM_FIELDS.FIRST_NAME, FORM_FIELDS.MIDDLE_NAME]);
        },
        [setValue, trigger],
    );

    const errorsInNameOrLastName = Boolean(errors[FORM_FIELDS.FIRST_NAME] || errors[FORM_FIELDS.LAST_NAME]);

    const noPatronymicChecked = useMemo(() => {
        if (errorsInNameOrLastName) {
            return false;
        }
        return patronymic === null;
    }, [patronymic, errorsInNameOrLastName]);

    const onChangeNoPatronymic: React.ComponentProps<typeof Checkbox>['onChange'] = useCallback(
        async (event) => {
            if (event.currentTarget.checked) {
                const fullName = [getValues(FORM_FIELDS.LAST_NAME), getValues(FORM_FIELDS.FIRST_NAME)]
                    .filter((v) => v)
                    .join(' ');
                setValue(FORM_FIELDS.MIDDLE_NAME, null);
                setValue(FORM_FIELDS.FULL_NAME, fullName);
            } else {
                setValue(FORM_FIELDS.MIDDLE_NAME, undefined);
            }
            await trigger(FORM_FIELDS.MIDDLE_NAME);
        },
        [trigger, setValue, getValues],
    );

    return (
        <Rows>
            <Row>
                <Col md-6>
                    <Controller
                        control={control}
                        name={FORM_FIELDS.MONTHLY_SALARY}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                label="Ежемесячный доход"
                                value={amountTransform.input(field)}
                                onChange={amountTransform.output(field)}
                                errorMessage={errors[FORM_FIELDS.MONTHLY_SALARY]?.message}
                            />
                        )}
                    />
                </Col>
                <Col md-6>
                    <Controller
                        control={control}
                        name={FORM_FIELDS.REGION}
                        render={({ field }) => (
                            <RegionField field={field} form={form} customLabel="Город получения карты" />
                        )}
                    />
                </Col>
            </Row>
            {showGosuslugiAuth && <GosuslugiAuth />}
            <Row>
                <Col>
                    <Controller
                        control={control}
                        name={FORM_FIELDS.FULL_NAME}
                        render={({ field: { onBlur, ...field } }) => (
                            <FullNameSuggest
                                input={{
                                    ...field,
                                    label: 'Фамилия, имя, отчество',
                                    errorMessage:
                                        errors[FORM_FIELDS.FIRST_NAME]?.message ||
                                        errors[FORM_FIELDS.LAST_NAME]?.message ||
                                        errors[FORM_FIELDS.MIDDLE_NAME]?.message,
                                }}
                                onSelect={onSelectFullName}
                                onChange={field.onChange}
                                onBlur={onBlur}
                            />
                        )}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Checkbox
                        checked={noPatronymicChecked}
                        onChange={onChangeNoPatronymic}
                        disabled={errorsInNameOrLastName}>
                        Отчество отсутствует
                    </Checkbox>
                </Col>
            </Row>

            {isLoggedIn && (
                <>
                    <Row>
                        <Col md-6>
                            <Controller
                                control={control}
                                name={FORM_FIELDS.EMAIL}
                                render={({ field }) => (
                                    <TextInput
                                        {...field}
                                        label="Электронная почта"
                                        errorMessage={errors[FORM_FIELDS.EMAIL]?.message}
                                    />
                                )}
                            />
                        </Col>
                        <Col md-6>
                            <Controller
                                control={control}
                                name={FORM_FIELDS.PHONE}
                                render={({ field }) => (
                                    <PhoneInput
                                        {...field}
                                        disabled
                                        label="Мобильный телефон"
                                        onChange={phoneTransform.output(field)}
                                        errorMessage={errors[FORM_FIELDS.PHONE]?.message}
                                    />
                                )}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md-8 />
                        <Col md-4>
                            <Button block variant="primary" type="submit">
                                Далее
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
        </Rows>
    );
};
